import { useIsDemo } from "@/demo/DemoContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CircleCheck, Inbox, ReceiptText, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate } from "@/lib/format";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import {
  EmptyState,
  ErrorState,
  HeaderRow,
  LoadingState,
  MerchantCell,
  TH,
  TableShell,
  receiptSourceLabel,
  rowLink,
} from "@/components/ExpenseUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";

type ReceiptResponse = components["schemas"]["ReceiptResponse"];

const PAGE_SIZE = 20;
const MAX_UPLOAD_FILES = 10;
const ACCEPTED = ["application/pdf", "image/"];
const POLL_MS = 3000;
// Matching runs just after extraction; the window stops polling forever on receipts waiting on a card transaction.
const SETTLING_MS = 2 * 60_000;
// A receipt stuck in Pending this long has stalled server-side; polling for it would never end.
const STALLED_MS = 10 * 60_000;

// Unmatched is the default tab: it's the only kind that needs a person. "No VAT number" is the reclaim chase list.

type TabValue = "unmatched" | "no-vat-number" | "all";

export function ReceiptsPage({ onReceiptClick }: { onReceiptClick?: (receiptId: string) => void } = {}) {
  const isDemo = useIsDemo();
  const api = useDolfinClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<TabValue>("unmatched");
  const [receipts, setReceipts] = useState<ReceiptResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [upload, setUpload] = useState<{ done: number; total: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A quiet fetch is the background poll: keeps the last good list on screen if a tick fails.
  const fetchReceipts = useCallback(async ({ quiet = false }: { quiet?: boolean } = {}) => {
    if (!quiet) {
      setLoading(true);
      setError(null);
    }
    const query: Record<string, string | number | boolean> = { Limit: PAGE_SIZE };
    if (tab === "unmatched") query.MatchStatus = "Unmatched";
    if (tab === "no-vat-number") query.MissingVatNumber = true;

    const { data, error: apiError } = await api.GET("/v1/receipts", { params: { query } });
    if (apiError || !data) {
      if (!quiet) setError("Couldn't load receipts.");
    } else {
      setReceipts(data.data ?? []);
    }
    if (!quiet) setLoading(false);
  }, [api, tab]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const inFlight = receipts.some(isInFlight);

  useEffect(() => {
    if (!inFlight) return;
    const id = setInterval(() => void fetchReceipts({ quiet: true }), POLL_MS);
    return () => clearInterval(id);
  }, [inFlight, fetchReceipts]);

  async function handleUpload(files: File[]) {
    const accepted = files.filter((f) => ACCEPTED.some((type) => f.type.startsWith(type)));
    if (accepted.length < files.length) {
      toast.error("Only PDFs and images can be uploaded as receipts.");
    }
    if (accepted.length > MAX_UPLOAD_FILES) {
      toast.error(`Up to ${MAX_UPLOAD_FILES} receipts at a time.`);
      return;
    }
    if (accepted.length === 0) return;

    let uploaded = 0;
    setUpload({ done: 0, total: accepted.length });
    // One at a time: each upload triggers extraction and matching, and a burst of parallel uploads
    // gains little while making a partial failure harder to report.
    for (const file of accepted) {
      const body = new FormData();
      body.append("file", file);
      // openapi-fetch would serialise a JSON body; multipart has to go through as-is.
      const { error: apiError } = await api.POST("/v1/receipts", {
        body: body as unknown as never,
        bodySerializer: (b: unknown) => b as BodyInit,
      });
      if (apiError) {
        toast.error(apiErrorMessage(apiError, `Couldn't upload ${file.name}.`));
      } else {
        uploaded += 1;
      }
      setUpload((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
    }

    if (uploaded > 0) {
      toast.success(
        uploaded === 1 ? "Receipt uploaded. Reading it now." : `${uploaded} receipts uploaded. Reading them now.`,
      );
      await fetchReceipts();
    }
    setUpload(null);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function retryExtraction(receiptId: string) {
    const { error: apiError } = await api.POST("/v1/receipts/{id}/retry-extraction", {
      params: { path: { id: receiptId } },
    });
    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Couldn't retry the extraction."));
    } else {
      toast.success("Reading the receipt again.");
      await fetchReceipts();
    }
  }

  const uploading = upload !== null;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Receipts"
        subtitle="Upload a receipt and Dolfin reads it, then pairs it with the matching card transaction automatically."
      />

      <input
        ref={fileInput}
        type="file"
        accept="application/pdf,image/*"
        multiple
        className="hidden"
        onChange={(e) => handleUpload(Array.from(e.target.files ?? []))}
      />
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload receipts"
        className={cn(
          "mb-5 rounded-xl border-2 border-dashed px-6 py-5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          dragOver
            ? "border-accent-blue/60 bg-accent-blue/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/50",
          uploading ? "pointer-events-none opacity-70" : "cursor-pointer",
        )}
        onClick={() => !uploading && fileInput.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && !uploading) fileInput.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!uploading) handleUpload(Array.from(e.dataTransfer.files));
        }}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-4" />
            {upload.total === 1 ? "Uploading receipt..." : `Uploading ${upload.done + 1} of ${upload.total}...`}
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
            <Upload className="size-5 text-muted-foreground" />
            <span className="font-medium">Drop receipts here, or click to browse</span>
            <span className="text-xs text-muted-foreground">
              Up to {MAX_UPLOAD_FILES} at once · PDF · PNG · JPG
            </span>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)} className="mb-4">
        <TabsList>
          <TabsTrigger value="unmatched">Inbox</TabsTrigger>
          <TabsTrigger value="no-vat-number">No VAT number</TabsTrigger>
          <TabsTrigger value="all">All receipts</TabsTrigger>
        </TabsList>
      </Tabs>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchReceipts()} />
      ) : loading ? (
        <LoadingState label="Loading receipts..." />
      ) : receipts.length === 0 ? (
        tab === "unmatched" ? (
          <EmptyState icon={Inbox} title="Inbox clear">
            Every uploaded receipt has been paired with a card transaction.
          </EmptyState>
        ) : tab === "no-vat-number" ? (
          <EmptyState icon={CircleCheck} title="No VAT numbers to chase">
            Every receipt carries a supplier VAT number, so nothing is blocking a reclaim.
          </EmptyState>
        ) : (
          <EmptyState icon={ReceiptText} title="No receipts yet">
            Drop a receipt above. Dolfin reads the merchant, total and VAT, then finds the card
            transaction it belongs to.
          </EmptyState>
        )
      ) : (
        <TableShell>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Merchant</TableHead>
                <TableHead className={TH}>Date</TableHead>
                <TableHead className={TH}>VAT number</TableHead>
                <TableHead className={TH}>Status</TableHead>
                <TableHead className={cn(TH, "text-right")}>VAT</TableHead>
                <TableHead className={cn(TH, "text-right")}>Total</TableHead>
                <TableHead className="w-10">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {receipts.map((receipt) => (
                <TableRow
                  key={receipt.id}
                  {...rowLink(onReceiptClick ? () => onReceiptClick(receipt.id!) : undefined)}
                >
                  <TableCell className="max-w-[280px]">
                    <MerchantCell
                      name={receipt.merchantName || receipt.fileName || "Untitled receipt"}
                      secondary={[
                        receiptSourceLabel(receipt.source),
                        receipt.merchantName ? receipt.fileName : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    />
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {formatDate(receipt.receiptDate ?? receipt.createdAt)}
                  </TableCell>
                  <TableCell>
                    {receipt.merchantVatNumber ? (
                      <span className="font-mono text-xs">{receipt.merchantVatNumber}</span>
                    ) : receipt.extractionStatus === "Extracted" ? (
                      <span className="text-xs text-amber-600">Missing</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ReceiptStatusBadge receipt={receipt} />
                  </TableCell>
                  <TableCell className="text-right text-sm whitespace-nowrap text-muted-foreground tabular-nums">
                    {receipt.taxTotal != null ? formatCurrency(receipt.taxTotal, receipt.currency) : "-"}
                  </TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap tabular-nums">
                    {receipt.total != null ? formatCurrency(receipt.total, receipt.currency) : "-"}
                  </TableCell>
                  <TableCell>
                    {receipt.extractionStatus === "Failed" && !isDemo ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label="Read again"
                        onClick={(e) => {
                          e.stopPropagation();
                          retryExtraction(receipt.id!);
                        }}
                      >
                        <RefreshCw className="size-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      )}
    </PageContainer>
  );
}

function isInFlight(receipt: ReceiptResponse) {
  const age = receipt.createdAt ? Date.now() - new Date(receipt.createdAt).getTime() : Infinity;
  if (receipt.extractionStatus === "Pending") return age < STALLED_MS;
  return receipt.extractionStatus === "Extracted" && receipt.matchStatus !== "Matched" && age < SETTLING_MS;
}

export function ReceiptStatusBadge({ receipt }: { receipt: ReceiptResponse }) {
  if (receipt.extractionStatus === "Failed") {
    return (
      <Badge variant="outline" className="border-destructive/30 bg-destructive/15 text-destructive">
        <AlertTriangle className="size-3" /> Unreadable
      </Badge>
    );
  }
  if (receipt.extractionStatus === "Pending") {
    return (
      <Badge variant="secondary">
        <Spinner className="size-3" /> Reading
      </Badge>
    );
  }
  if (receipt.matchStatus === "Matched") {
    return (
      <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/15 text-emerald-600">
        Matched
      </Badge>
    );
  }
  return <Badge variant="secondary">Waiting to match</Badge>;
}
